<template>
    <div class="flex w-full h-7 rounded-md overflow-hidden">
        <div
            v-for="(segment, i) in segments"
            :key="i"
            class="flex items-center justify-center details font-medium"
            :class="[segment.colorClass, segment.textColorClass]"
            :style="{ width: segment.percent + '%' }"
            aria-hidden="true"
            :title="
                t('assessments.results.distribution.segmentTitle', {
                    score: formatNumber(i + 1),
                    count: formatNumber(segment.count),
                    percent: formatNumber(segment.percent / 100, 'percent'),
                })
            ">
            <span v-if="segment.percent >= 6">{{ formatNumber(segment.percent / 100, "percent") }}</span>
        </div>
        <div v-if="totalCount === 0" class="flex-1 bg-surface-sunken" aria-hidden="true"></div>
        <!-- The coloured segments say nothing to a screen reader, and `title` is not reliably announced. -->
        <ul class="sr-only" :aria-label="listLabel">
            <li v-for="(segment, i) in segments" :key="i">
                {{
                    t("assessments.results.distribution.segmentSummary", {
                        score: formatNumber(i + 1),
                        count: segment.count,
                        percent: formatNumber(segment.percent / 100, "percent"),
                    })
                }}
            </li>
        </ul>
    </div>
</template>

<script setup lang="ts">
import { formatNumber } from "~vue/utils/formatNumber";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import { paletteColor, paletteTextColor } from "./distributionPalette";

const { t } = useI18n();

interface Props {
    distribution: number[];
    // The question or section this chart belongs to; every chart's list would otherwise share one name.
    label?: string | null;
    maxScore?: number | null;
}

const props = defineProps<Props>();

const listLabel = computed(() => (props.label ? t("assessments.results.distribution.listLabelFor", { label: props.label }) : t("assessments.results.distribution.listLabel")));

const totalCount = computed(() => props.distribution.reduce((a, b) => a + b, 0));

const segments = computed(() => {
    const total = props.distribution.length;
    return props.distribution.map((count, i) => ({
        count,
        percent: totalCount.value > 0 ? (count / totalCount.value) * 100 : 0,
        colorClass: paletteColor(i, total),
        textColorClass: paletteTextColor(i, total),
    }));
});
</script>
